import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Filter } from "lucide-react"
import { PROPERTY_TYPE_CONFIG, PROPERTY_TYPE_VALUES } from "@/lib/project-configs"
import { UserDisplay } from "@/components/user-display"
import type { ProjectTag } from "@/types"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  advancedFilters: Record<string, string>
  activeAdvancedCount: number
  setAdvanced: (key: string, value: string) => void
  setAdvancedDebounced: (key: string, value: string) => void
  resetAdvancedFilters: () => void
  users: { id: string; first_name: string; last_name: string }[]
  teams: { id: string; name: string }[]
  project_tags: ProjectTag[]
}

export function AdvancedFiltersPopover({
  open,
  onOpenChange,
  advancedFilters,
  activeAdvancedCount,
  setAdvanced,
  setAdvancedDebounced,
  resetAdvancedFilters,
  users,
  teams,
  project_tags,
}: Props) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="relative h-[38px]">
          <Filter className="size-4" />
          Filtres
          {activeAdvancedCount > 0 && (
            <Badge className="ml-2 size-5 justify-center rounded-full p-0 text-xs">{activeAdvancedCount}</Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-96 max-h-[var(--radix-popover-content-available-height)] overflow-y-auto space-y-4 p-4">
        {/* Tags */}
        {project_tags.length > 0 && (
          <div className="space-y-1.5">
            <Label className="text-xs">Tags</Label>
            <div className="flex flex-wrap gap-1.5">
              {project_tags.map((tag) => {
                const isSelected = advancedFilters.project_tags_id_in === tag.id
                return (
                  <button
                    key={tag.id}
                    onClick={() => setAdvanced("project_tags_id_in", isSelected ? "" : tag.id)}
                    className="cursor-pointer rounded-full border px-2 py-0.5 text-xs font-medium transition-all"
                    style={{
                      borderColor: tag.color,
                      backgroundColor: isSelected ? tag.color : "transparent",
                      color: isSelected ? "#fff" : tag.color,
                    }}
                  >
                    {tag.name}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Surface terrain */}
        <div className="space-y-1.5">
          <Label className="text-xs">Surface terrain (m²)</Label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              placeholder="Min"
              className="h-8"
              value={advancedFilters.field_surface_gteq || ""}
              onChange={(e) => setAdvancedDebounced("field_surface_gteq", e.target.value)}
            />
            <span className="text-muted-foreground text-xs">—</span>
            <Input
              type="number"
              placeholder="Max"
              className="h-8"
              value={advancedFilters.field_surface_lteq || ""}
              onChange={(e) => setAdvancedDebounced("field_surface_lteq", e.target.value)}
            />
          </div>
        </div>

        {/* Surface bâti */}
        <div className="space-y-1.5">
          <Label className="text-xs">Surface bâti (m²)</Label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              placeholder="Min"
              className="h-8"
              value={advancedFilters.house_surface_gteq || ""}
              onChange={(e) => setAdvancedDebounced("house_surface_gteq", e.target.value)}
            />
            <span className="text-muted-foreground text-xs">—</span>
            <Input
              type="number"
              placeholder="Max"
              className="h-8"
              value={advancedFilters.house_surface_lteq || ""}
              onChange={(e) => setAdvancedDebounced("house_surface_lteq", e.target.value)}
            />
          </div>
        </div>

        {/* Créé par / Équipe */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Créé par</Label>
            <Select
              value={advancedFilters.created_by_id_eq || ""}
              onValueChange={(v) => setAdvanced("created_by_id_eq", v === "all" ? "" : v)}
            >
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Tous" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    <UserDisplay user={u} size="sm" />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Équipe</Label>
            <Select
              value={advancedFilters.assigned_teams_id_eq || ""}
              onValueChange={(v) => setAdvanced("assigned_teams_id_eq", v === "all" ? "" : v)}
            >
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Toutes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                {teams.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Type de bien */}
        <div className="space-y-1.5">
          <Label className="text-xs">Type de bien</Label>
          <Select
            value={advancedFilters.property_type_eq || ""}
            onValueChange={(v) => setAdvanced("property_type_eq", v === "all" ? "" : v)}
          >
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Tous" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              {Object.entries(PROPERTY_TYPE_CONFIG).map(([key, cfg]) => {
                const Icon = cfg.icon
                return (
                  <SelectItem key={key} value={PROPERTY_TYPE_VALUES[key]}>
                    <span className="flex items-center gap-2">
                      <Icon className="size-3.5" />
                      {cfg.label}
                    </span>
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
        </div>

        {/* Date de création */}
        <div className="space-y-1.5">
          <Label className="text-xs">Date de création</Label>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              className="h-8"
              value={advancedFilters.created_at_gteq || ""}
              onChange={(e) => setAdvanced("created_at_gteq", e.target.value)}
            />
            <span className="text-muted-foreground text-xs">—</span>
            <Input
              type="date"
              className="h-8"
              value={advancedFilters.created_at_lteq || ""}
              onChange={(e) => setAdvanced("created_at_lteq", e.target.value)}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
